/**
 * Implementation Guide Registry Parser
 *
 * Parses content/implementation-guide.md — the single source of truth
 * for the LMOS transformation roadmap.
 */

import yaml from 'js-yaml';
import type {
  ImplementationRegistry,
  ImplProgrammeInfo,
  ImplTransformationShift,
  ImplSystemDefinition,
  ImplWorkstream,
  ImplPhaseData,
  ImplIntegrationFlow,
  ImplRisk,
  ImplPhaseStatus,
  ImplIntegrationStatus,
  ImplIntegrationDirection,
  ImplRiskProbability,
  ImplRiskImpact,
  ImplPriority,
  ImplPhaseMilestone,
} from '../types';

import guideMarkdown from '../../content/implementation-guide.md?raw';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseYamlBlock(content: string): Record<string, unknown> | null {
  const match = content.match(/```yaml\n([\s\S]*?)\n```/);
  if (!match) return null;
  try {
    return yaml.load(match[1]) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v.trim() : fallback;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' ? v : fallback;
}

function bool(v: unknown): boolean {
  return v === true;
}

function strArr(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => str(x)).filter(Boolean);
}

/** Extract the content of a top-level ## section */
function extractSection(markdown: string, heading: string): string {
  const pattern = new RegExp(
    `## ${heading}[\\s\\S]*?(?=\\n## |$)`,
  );
  const m = markdown.match(pattern);
  return m ? m[0] : '';
}

/** Extract the content of a #### subsection within a block */
function extractSubsection(content: string, heading: string): string {
  const pattern = new RegExp(
    `#### ${heading}[\\s\\S]*?(?=\\n####|\\n###|$)`,
    'i',
  );
  const m = content.match(pattern);
  if (!m) return '';
  return m[0].replace(new RegExp(`#### ${heading}[^\\n]*\\n`, 'i'), '').trim();
}

/** Parse a flat bullet list from a markdown text block */
function parseBullets(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.match(/^[-*]\s+(.+)$/))
    .filter(Boolean)
    .map((m) => m![1].trim());
}

/** Parse milestones: "Text — Date" or "Text — WeekN" */
function parseMilestones(text: string): ImplPhaseMilestone[] {
  return parseBullets(text).map((line) => {
    const parts = line.split(/\s+[—–-]\s+/);
    return {
      title: parts[0]?.trim() ?? line,
      date: parts[1]?.trim() ?? '',
    };
  });
}

// ---------------------------------------------------------------------------
// Section parsers
// ---------------------------------------------------------------------------

function parseInfo(markdown: string): ImplProgrammeInfo {
  const section = extractSection(markdown, 'DOCUMENT INFO');
  const raw = parseYamlBlock(section) as Record<string, unknown> | null;
  if (!raw) {
    return {
      version: '2.0.0',
      lastUpdated: '',
      programmeStart: '',
      pilotDeadline: '',
      description: '',
      descriptionAr: '',
      totalOwnedBranches: 0,
      totalSubDealers: 0,
      totalBrands: 0,
      programDurationWeeks: 0,
    };
  }
  const info = (raw.info ?? raw) as Record<string, unknown>;
  const scale = (info.scale ?? {}) as Record<string, unknown>;
  return {
    version: str(info.version, '2.0.0'),
    lastUpdated: str(info.lastUpdated),
    programmeStart: str(info.programmeStart),
    pilotDeadline: str(info.pilotDeadline),
    description: str(info.description),
    descriptionAr: str(info.descriptionAr),
    totalOwnedBranches: num(scale.totalOwnedBranches, 16),
    totalSubDealers: num(scale.totalSubDealers, 23),
    totalBrands: num(scale.totalBrands ?? info.totalBrands, 3),
    programDurationWeeks: num(scale.programDurationWeeks ?? info.programDurationWeeks, 38),
  };
}

function parseCriticalShifts(markdown: string): ImplTransformationShift[] {
  const section = extractSection(markdown, 'TRANSFORMATION CONTEXT');
  const raw = parseYamlBlock(section) as Record<string, unknown> | null;
  if (!raw) return [];
  const ctx = (raw.transformationContext ?? raw) as Record<string, unknown>;
  const shifts = ctx.criticalShifts as Array<Record<string, string>> | undefined;
  if (!Array.isArray(shifts)) return [];
  return shifts.map((s) => ({ from: str(s.from), to: str(s.to) }));
}

function parseSystems(markdown: string): ImplSystemDefinition[] {
  const section = extractSection(markdown, 'SYSTEM TOPOLOGY');
  const raw = parseYamlBlock(section) as Record<string, unknown> | null;
  if (!raw) return [];
  const topology = (raw.systemTopology ?? raw) as Record<string, unknown>;
  const systems = topology.systems as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(systems)) return [];

  return systems.map((s) => ({
    id: str(s.id),
    name: str(s.name),
    nameAr: str(s.nameAr),
    role: str(s.role),
    roleAr: str(s.roleAr),
    owner: str(s.owner),
    ownerAr: str(s.ownerAr),
    currentState: str(s.currentState),
    systemType: str(s.systemType),
    users: strArr(s.users),
    pipelineStagesOwned: strArr(s.pipelineStagesOwned),
    exitTrigger: str(s.exitTrigger),
    criticalShift: str(s.criticalShift),
  }));
}

function parseWorkstreams(markdown: string): ImplWorkstream[] {
  const section = extractSection(markdown, 'WORKSTREAMS');
  const raw = parseYamlBlock(section) as Record<string, unknown> | null;
  if (!raw) return [];
  const ws = (raw.workstreams ?? raw) as Record<string, unknown>;
  const tracks = (ws.tracks ?? ws) as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(tracks)) return [];

  return tracks.map((t) => ({
    id: str(t.id),
    label: str(t.label),
    labelAr: str(t.labelAr),
    color: str(t.color, '#6366f1'),
    owner: str(t.owner),
    description: str(t.description),
    activePhases: strArr(t.activePhases),
  }));
}

function parsePhases(markdown: string): ImplPhaseData[] {
  const phasesSection = extractSection(markdown, 'PHASES');
  // Split on ### PHASE N:
  const phaseBlocks = phasesSection.split(/(?=### PHASE \d+:)/);

  return phaseBlocks
    .filter((block) => /### PHASE \d+:/.test(block))
    .map((block): ImplPhaseData | null => {
      const raw = parseYamlBlock(block) as Record<string, unknown> | null;
      if (!raw) return null;
      const meta = (raw.meta ?? {}) as Record<string, unknown>;

      const descText = extractSubsection(block, 'Why This Phase Exists');
      const objText = extractSubsection(block, 'Objectives');
      const delivText = extractSubsection(block, 'Key Deliverables');
      const tasksText = extractSubsection(block, 'Tasks');
      const milestonesText = extractSubsection(block, 'Milestones');
      const gateText = extractSubsection(block, 'Phase Gate Criteria');

      return {
        meta: {
          id: str(meta.id),
          number: num(meta.number),
          titleEn: str(meta.titleEn),
          titleAr: str(meta.titleAr),
          subtitleEn: str(meta.subtitleEn),
          subtitleAr: str(meta.subtitleAr),
          startDate: str(meta.startDate),
          endDate: str(meta.endDate),
          durationWeeks: num(meta.durationWeeks),
          status: str(meta.status, 'not_started') as ImplPhaseStatus,
          progress: num(meta.progress),
          color: str(meta.color, '#6366f1'),
          icon: str(meta.icon),
          activeWorkstreams: strArr(meta.activeWorkstreams),
          dependsOn: strArr(meta.dependsOn),
        },
        description: descText,
        objectives: parseBullets(objText),
        deliverables: parseBullets(delivText),
        tasks: parseBullets(tasksText),
        milestones: parseMilestones(milestonesText),
        gateCriteria: parseBullets(gateText),
      };
    })
    .filter((p): p is ImplPhaseData => p !== null);
}

function parseIntegrations(markdown: string): ImplIntegrationFlow[] {
  const section = extractSection(markdown, 'INTEGRATION REGISTER');
  const raw = parseYamlBlock(section) as Record<string, unknown> | null;
  if (!raw) return [];
  const reg = (raw.integrationRegister ?? raw) as Record<string, unknown>;
  const list = reg.integrations as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(list)) return [];

  return list.map((i) => ({
    id: str(i.id),
    name: str(i.name),
    source: str(i.source),
    target: str(i.target),
    direction: str(i.direction, 'inbound') as ImplIntegrationDirection,
    priority: str(i.priority, 'medium') as ImplPriority,
    owner: str(i.owner),
    phaseDue: str(i.phaseDue),
    status: str(i.status, 'needs_build') as ImplIntegrationStatus,
    cdpGoldenRecordRequired: bool(i.cdpGoldenRecordRequired),
    description: str(i.description),
    triggerCondition: str(i.triggerCondition),
    errorHandling: str(i.errorHandling),
  }));
}

function parseRisks(markdown: string): ImplRisk[] {
  const section = extractSection(markdown, 'RISK REGISTER');
  const raw = parseYamlBlock(section) as Record<string, unknown> | null;
  if (!raw) return [];
  const reg = (raw.riskRegister ?? raw) as Record<string, unknown>;
  const list = reg.risks as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(list)) return [];

  return list.map((r) => ({
    id: str(r.id),
    title: str(r.title),
    titleAr: str(r.titleAr),
    category: str(r.category),
    probability: str(r.probability, 'medium') as ImplRiskProbability,
    impact: str(r.impact, 'medium') as ImplRiskImpact,
    description: str(r.description),
    mitigation: strArr(r.mitigation),
    owner: str(r.owner),
    escalateTo: str(r.escalateTo),
  }));
}

// ---------------------------------------------------------------------------
// Main registry builder
// ---------------------------------------------------------------------------

let _registry: ImplementationRegistry | null = null;

export function getImplementationRegistry(): ImplementationRegistry {
  if (_registry) return _registry;

  _registry = {
    info: parseInfo(guideMarkdown),
    criticalShifts: parseCriticalShifts(guideMarkdown),
    systems: parseSystems(guideMarkdown),
    workstreams: parseWorkstreams(guideMarkdown),
    phases: parsePhases(guideMarkdown),
    integrations: parseIntegrations(guideMarkdown),
    risks: parseRisks(guideMarkdown),
  };

  return _registry;
}

// ---------------------------------------------------------------------------
// Convenience accessors
// ---------------------------------------------------------------------------

export function getPhaseById(id: string): ImplPhaseData | undefined {
  return getImplementationRegistry().phases.find((p) => p.meta.id === id);
}

export function getWorkstreamById(id: string): ImplWorkstream | undefined {
  return getImplementationRegistry().workstreams.find((w) => w.id === id);
}

export function getOverallProgress(): number {
  const phases = getImplementationRegistry().phases;
  if (!phases.length) return 0;
  return Math.round(phases.reduce((s, p) => s + p.meta.progress, 0) / phases.length);
}

export function getPhaseStatusColor(status: ImplPhaseStatus): string {
  const map: Record<ImplPhaseStatus, string> = {
    completed: 'bg-green-100 text-green-700',
    in_progress: 'bg-blue-100 text-blue-700',
    not_started: 'bg-gray-100 text-gray-500',
    at_risk: 'bg-red-100 text-red-700',
  };
  return map[status];
}

export function getPhaseStatusLabel(status: ImplPhaseStatus, isAr: boolean): string {
  const map: Record<ImplPhaseStatus, { en: string; ar: string }> = {
    completed: { en: 'Completed', ar: 'مكتمل' },
    in_progress: { en: 'In Progress', ar: 'جارٍ' },
    not_started: { en: 'Not Started', ar: 'لم يبدأ' },
    at_risk: { en: 'At Risk', ar: 'في خطر' },
  };
  return isAr ? map[status].ar : map[status].en;
}

export function getIntegrationStatusLabel(status: ImplIntegrationStatus): string {
  const map: Record<ImplIntegrationStatus, string> = {
    live: 'Live',
    partial: 'Partial',
    ready_to_configure: 'Ready to Configure',
    needs_build: 'Needs Build',
    needs_configuration: 'Needs Configuration',
    exists_needs_configuration: 'Exists — Configure',
  };
  return map[status];
}

export function getIntegrationStatusColor(status: ImplIntegrationStatus): string {
  const map: Record<ImplIntegrationStatus, string> = {
    live: 'bg-green-100 text-green-700',
    partial: 'bg-yellow-100 text-yellow-700',
    ready_to_configure: 'bg-blue-100 text-blue-700',
    needs_build: 'bg-red-100 text-red-700',
    needs_configuration: 'bg-orange-100 text-orange-700',
    exists_needs_configuration: 'bg-purple-100 text-purple-700',
  };
  return map[status];
}

export function getPriorityColor(priority: ImplPriority): string {
  const map: Record<ImplPriority, string> = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-gray-100 text-gray-600',
  };
  return map[priority];
}

export function getRiskImpactColor(impact: ImplRiskImpact): string {
  const map: Record<ImplRiskImpact, string> = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  return map[impact];
}
